import React, { useEffect, useState } from 'react'
import {
	Drawer,
	Form,
	Input,
	InputNumber,
	Select,
	Button,
	Row,
	Col,
	Divider,
	Typography,
	message,
	Space,
} from 'antd'
import { MessagingPrice, VendorType, CurrencyType, ChannelType } from 'types'
import pricingService from 'api/pricing/pricingService'

const { Option } = Select
const { Title } = Typography

interface PricingFormProps {
	visible: boolean
	editingRecord: MessagingPrice | null
	onSubmit: () => void
	onCancel: () => void
}

/**
 * Pricing Form Component
 * Handles creation and editing of pricing entries
 */
const PricingForm: React.FC<PricingFormProps> = ({
	visible,
	editingRecord,
	onSubmit,
	onCancel,
}) => {
	const [form] = Form.useForm()
	const [loading, setLoading] = useState(false)
	const [countries, setCountries] = useState<
		Array<{ code: string; name: string; phone_code: string }>
	>([])

	/**
	 * Vendor options
	 */
	const vendorOptions: Array<{ value: VendorType; label: string }> = [
		{ value: 'GUPSHUP', label: 'Gupshup' },
		{ value: 'GUPSHUP_ENTERPRISE', label: 'Gupshup Enterprise' },
		{ value: 'THREE_SIXTY_DIALOG', label: '360Dialog' },
		{ value: 'WHATSAPP_CLOUD', label: 'WhatsApp Cloud' },
		{ value: 'AIRTEL', label: 'Airtel' },
		{ value: 'THREE_SIXTY_DIALOG_CLOUD', label: '360Dialog Cloud' },
		{ value: 'PINNACLE', label: 'Pinnacle' },
		{ value: 'PINNACLE_V3', label: 'Pinnacle V3' },
	]

	/**
	 * Currency options
	 */
	const currencyOptions: Array<{
		value: CurrencyType
		label: string
		symbol: string
	}> = [
		{ value: 'USD', label: 'US Dollar', symbol: '$' },
		{ value: 'INR', label: 'Indian Rupee', symbol: '₹' },
		{ value: 'EUR', label: 'Euro', symbol: '€' },
		{ value: 'IDR', label: 'Indonesian Rupiah', symbol: 'Rp' },
		{ value: 'AUD', label: 'Australian Dollar', symbol: 'A$' },
		{ value: 'GBP', label: 'British Pound', symbol: '£' },
	]

	/**
	 * Channel options
	 */
	const channelOptions: Array<{ value: ChannelType; label: string }> = [
		{ value: 'WHATSAPP', label: 'WhatsApp' },
		{ value: 'BIZ_CONVERSATION', label: 'Business Conversation' },
	]

	/**
	 * Fetch countries for dropdown
	 */
	const fetchCountries = async () => {
		try {
			const countriesData = await pricingService.getCountries()
			setCountries(countriesData)
		} catch (error) {
			// Fallback to common countries
			setCountries([
				{ code: 'IN', name: 'India', phone_code: '91' },
				{ code: 'US', name: 'United States', phone_code: '1' },
				{ code: 'GB', name: 'United Kingdom', phone_code: '44' },
				{ code: 'AU', name: 'Australia', phone_code: '61' },
				{ code: 'ID', name: 'Indonesia', phone_code: '62' },
				{ code: 'DE', name: 'Germany', phone_code: '49' },
				{ code: 'FR', name: 'France', phone_code: '33' },
				{ code: 'CA', name: 'Canada', phone_code: '1' },
				{ code: 'BR', name: 'Brazil', phone_code: '55' },
				{ code: 'MX', name: 'Mexico', phone_code: '52' },
			])
		}
	}

	/**
	 * Handle form submission
	 */
	const handleSubmit = async (values: any) => {
		setLoading(true)
		try {
			if (editingRecord) {
				await pricingService.updatePricing(editingRecord._id!, values)
				message.success('Pricing updated successfully')
			} else {
				await pricingService.createPricing(values)
				message.success('Pricing created successfully')
			}
			onSubmit()
		} catch (error) {
			message.error('Failed to save pricing')
		} finally {
			setLoading(false)
		}
	}

	/**
	 * Handle form cancellation
	 */
	const handleCancel = () => {
		form.resetFields()
		onCancel()
	}

	/**
	 * Handle country selection change
	 */
	const handleCountryChange = (countryCode: string) => {
		const selectedCountry = countries.find(c => c.code === countryCode)
		if (selectedCountry) {
			form.setFieldsValue({
				phone_code: selectedCountry.phone_code,
			})
		}
	}

	/**
	 * Reset form when visibility changes
	 */
	useEffect(() => {
		if (visible) {
			if (editingRecord) {
				form.setFieldsValue(editingRecord)
			} else {
				form.resetFields()
			}
			fetchCountries()
		}
	}, [visible, editingRecord])

	return (
		<Drawer
			title={editingRecord ? 'Edit Pricing Entry' : 'Add New Pricing Entry'}
			visible={visible}
			onClose={handleCancel}
			width={800}
			destroyOnClose
		>
			<Form
				form={form}
				layout='vertical'
				onFinish={handleSubmit}
				initialValues={{
					channel: 'WHATSAPP',
					base_currency: 'USD',
					vendor: 'THREE_SIXTY_DIALOG',
				}}
			>
				{/* Basic Information */}
				<Title level={4}>Basic Information</Title>

				<Row gutter={16}>
					<Col span={12}>
						<Form.Item
							label='Country'
							name='country_code'
							rules={[{ required: true, message: 'Country is required' }]}
						>
							<Select
								placeholder='Select Country'
								showSearch
								optionFilterProp='children'
								filterOption={(input, option) =>
									(option?.children as unknown as string)
										?.toLowerCase()
										.includes(input.toLowerCase())
								}
								onChange={handleCountryChange}
							>
								{countries.map(country => (
									<Option key={country.code} value={country.code}>
										{country.name} ({country.code})
									</Option>
								))}
							</Select>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							label='Phone Code'
							name='phone_code'
							rules={[{ required: true, message: 'Phone code is required' }]}
						>
							<Input placeholder='e.g., 91' />
						</Form.Item>
					</Col>
				</Row>

				<Row gutter={16}>
					<Col span={12}>
						<Form.Item
							label='Country Name'
							name='country'
							rules={[{ required: true, message: 'Country name is required' }]}
						>
							<Input placeholder='e.g., India' />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							label='Channel'
							name='channel'
							rules={[{ required: true, message: 'Channel is required' }]}
						>
							<Select placeholder='Select Channel'>
								{channelOptions.map(option => (
									<Option key={option.value} value={option.value}>
										{option.label}
									</Option>
								))}
							</Select>
						</Form.Item>
					</Col>
				</Row>

				<Row gutter={16}>
					<Col span={12}>
						<Form.Item
							label='Vendor'
							name='vendor'
							rules={[{ required: true, message: 'Vendor is required' }]}
						>
							<Select placeholder='Select Vendor'>
								{vendorOptions.map(option => (
									<Option key={option.value} value={option.value}>
										{option.label}
									</Option>
								))}
							</Select>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							label='Base Currency'
							name='base_currency'
							rules={[{ required: true, message: 'Currency is required' }]}
						>
							<Select placeholder='Select Currency'>
								{currencyOptions.map(option => (
									<Option key={option.value} value={option.value}>
										{option.symbol} {option.label}
									</Option>
								))}
							</Select>
						</Form.Item>
					</Col>
				</Row>

				<Divider />

				{/* Core Pricing */}
				<Title level={4}>Core Pricing</Title>

				<Row gutter={16}>
					<Col span={12}>
						<Form.Item
							label='Vendor Charge'
							name='vendor_charge'
							rules={[{ required: true, message: 'Vendor charge is required' }]}
						>
							<InputNumber
								style={{ width: '100%' }}
								precision={4}
								min={0}
								step={0.001}
								placeholder='0.0000'
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							label='WhatCX Charge'
							name='whatcx_charge'
							rules={[{ required: true, message: 'WhatCX charge is required' }]}
						>
							<InputNumber
								style={{ width: '100%' }}
								precision={4}
								min={0}
								step={0.001}
								placeholder='0.0000'
							/>
						</Form.Item>
					</Col>
				</Row>

				<Row gutter={16}>
					<Col span={12}>
						<Form.Item label='UIC Charge' name='uic_charge'>
							<InputNumber
								style={{ width: '100%' }}
								precision={4}
								min={0}
								step={0.001}
								placeholder='0.0000'
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item label='BIC Charge' name='bic_charge'>
							<InputNumber
								style={{ width: '100%' }}
								precision={4}
								min={0}
								step={0.001}
								placeholder='0.0000'
							/>
						</Form.Item>
					</Col>
				</Row>

				<Row gutter={16}>
					<Col span={12}>
						<Form.Item
							label='Vendor Per Message (Incoming)'
							name='vendor_per_message_incoming'
						>
							<InputNumber
								style={{ width: '100%' }}
								precision={4}
								min={0}
								step={0.001}
								placeholder='0.0000'
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							label='Vendor Per Message (Outgoing)'
							name='vendor_per_message_outgoing'
						>
							<InputNumber
								style={{ width: '100%' }}
								precision={4}
								min={0}
								step={0.001}
								placeholder='0.0000'
							/>
						</Form.Item>
					</Col>
				</Row>

				<Form.Item label='Media Charge (per 5MB)' name='media_charge'>
					<InputNumber
						style={{ width: '100%' }}
						precision={4}
						min={0}
						step={0.001}
						placeholder='0.0000'
					/>
				</Form.Item>

				<Divider />

				{/* Template Charges */}
				<Title level={4}>Template Charges</Title>

				<Row gutter={16}>
					<Col span={12}>
						<Form.Item label='Marketing Template' name='whatsapp_template_marketing'>
							<InputNumber
								style={{ width: '100%' }}
								precision={4}
								min={0}
								step={0.001}
								placeholder='0.0000'
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							label='Authentication Template'
							name='whatsapp_template_authentication'
						>
							<InputNumber
								style={{ width: '100%' }}
								precision={4}
								min={0}
								step={0.001}
								placeholder='0.0000'
							/>
						</Form.Item>
					</Col>
				</Row>

				<Row gutter={16}>
					<Col span={12}>
						<Form.Item label='Utility Template' name='whatsapp_template_utility'>
							<InputNumber
								style={{ width: '100%' }}
								precision={4}
								min={0}
								step={0.001}
								placeholder='0.0000'
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item label='WhatsApp Service' name='whatsapp_service'>
							<InputNumber
								style={{ width: '100%' }}
								precision={4}
								min={0}
								step={0.001}
								placeholder='0.0000'
							/>
						</Form.Item>
					</Col>
				</Row>

				<Divider />

				{/* Form Actions */}
				<Row justify='end'>
					<Space>
						<Button onClick={handleCancel}>Cancel</Button>
						<Button type='primary' htmlType='submit' loading={loading}>
							{editingRecord ? 'Update Pricing' : 'Create Pricing'}
						</Button>
					</Space>
				</Row>
			</Form>
		</Drawer>
	)
}

export default PricingForm
